/** The single source of truth for colour. CSS variables are generated from these tokens, and contrast is unit-tested. */
export type ThemeId = 'classic' | 'purple' | 'green' | 'light';
export type Density = 'compact' | 'comfortable';

export interface ThemeTokens {
  label: string;
  scheme: 'dark' | 'light';
  bg: string;
  panel: string;
  panel2: string;
  border: string;
  text: string;
  muted: string;
  /** Brand accent: buttons, focus, selection. */
  accent: string;
  accentText: string;
  accent2: string;
  up: string;
  down: string;
  warn: string;
  /**
   * The chart's own colours, when they differ from the panels around it (the classic look: a black chart in light windows).
   * Left out, the chart takes the panel colours.
   */
  chart?: ChartTokens;
}

export interface ChartTokens {
  bg: string;
  text: string;
  grid: string;
  /** Profit, buy and position lines drawn on the chart. */
  up: string;
  /** Loss, sell and stop lines drawn on the chart. */
  down: string;
  /** Bars and candle outlines going up and down. */
  barUp: string;
  barDown: string;
  /** Candle bodies. */
  bullBody: string;
  bearBody: string;
  /** The line chart. */
  line: string;
  /** The bid and ask lines. */
  bid: string;
  ask: string;
}

export const THEMES: Record<ThemeId, ThemeTokens> = {
  // The trading-terminal look: light, square Windows-style windows and a black chart with lime bars.
  classic: {
    label: 'Classic',
    scheme: 'light',
    bg: '#f0f0f0',
    panel: '#ffffff',
    panel2: '#f0f0f0',
    border: '#c8c8c8',
    text: '#000000',
    muted: '#505050',
    accent: '#0060c0',
    accentText: '#ffffff',
    accent2: '#1a5fb4',
    up: '#0a6e28',
    down: '#c0141e',
    warn: '#875400',
    chart: {
      bg: '#000000',
      text: '#ffffff',
      grid: '#5a5a5a',
      up: '#20c040',
      down: '#ff4040',
      barUp: '#00ff00',
      barDown: '#00ff00',
      bullBody: '#000000',
      bearBody: '#ffffff',
      line: '#00ff00',
      bid: '#a0a0a0',
      ask: '#ff0000',
    },
  },
  purple: {
    label: 'Adiox Purple',
    scheme: 'dark',
    bg: '#0c0918',
    panel: '#140f27',
    panel2: '#1c1538',
    border: '#31275a',
    text: '#efeaff',
    muted: '#a79fd0',
    accent: '#ff5fb1',
    accentText: '#1a0716',
    accent2: '#8b5cf6',
    up: '#34e0a1',
    down: '#ff6b7f',
    warn: '#ffc857',
  },
  green: {
    label: 'Adiox Green',
    scheme: 'dark',
    bg: '#040704',
    panel: '#0a110b',
    panel2: '#111b12',
    border: '#213826',
    text: '#e8f6ea',
    muted: '#8fae95',
    accent: '#2fd46b',
    accentText: '#03140a',
    accent2: '#19a34f',
    up: '#4ade80',
    down: '#ff6f6f',
    warn: '#ffc857',
  },
  light: {
    label: 'Adiox Light',
    scheme: 'light',
    bg: '#f4f2fb',
    panel: '#ffffff',
    panel2: '#ece8f8',
    border: '#d5cfe8',
    text: '#1b1633',
    muted: '#5f5883',
    accent: '#c4177a',
    accentText: '#ffffff',
    accent2: '#6d3fd6',
    up: '#0b7a54',
    down: '#c72440',
    warn: '#8a5a00',
  },
};

export const DEFAULT_THEME: ThemeId = 'classic';
export const DEFAULT_DENSITY: Density = 'comfortable';

const CHART_KEYS: (keyof ChartTokens)[] = [
  'bg',
  'text',
  'grid',
  'up',
  'down',
  'barUp',
  'barDown',
  'bullBody',
  'bearBody',
  'line',
  'bid',
  'ask',
];

/** accentText -> accent-text, panel2 -> panel-2 (the names the stylesheet reads). */
export const cssName = (key: string) =>
  key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`).replace(/([a-z])(\d)/g, '$1-$2');

/** Applies a theme and density to the document without a reload. */
export function applyTheme(
  theme: ThemeId,
  density: Density,
  root: HTMLElement = document.documentElement,
): void {
  const tokens = THEMES[theme];
  for (const [key, value] of Object.entries(tokens)) {
    if (key !== 'label' && key !== 'scheme' && key !== 'chart') {
      root.style.setProperty(`--${cssName(key)}`, value as string);
    }
  }
  // The chart's own colours, or none (the chart then follows the panels).
  for (const key of CHART_KEYS) {
    const value = tokens.chart?.[key];
    if (value) root.style.setProperty(`--chart-${cssName(key)}`, value);
    else root.style.removeProperty(`--chart-${cssName(key)}`);
  }
  root.style.colorScheme = tokens.scheme;
  root.dataset.theme = theme;
  root.dataset.density = density;
}

// ---------------------------------------------------------------- WCAG contrast
function channel(value: number): number {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

export function luminance(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  return 0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255);
}

export function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}
